/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import {
  CellEditRequestEvent,
  ColDef,
  IDetailCellRendererParams,
} from "ag-grid-community";
import { AgGridReact, CustomCellEditorProps } from "ag-grid-react";
import {
  CellEditRequestValue,
  ParamsEditorRowDatum,
} from "components/ToolEditor/ToolEditor.types";
import { useCallback, useMemo } from "react";
import { CellEditorFileFormats } from "./CellEditorFileFormats";
import { isFileFormatsV3, isFileTagsV3 } from "types/json-schemas/type_guards";
import { MultiBadgeRenderer } from "components/ToolEditor/components/MultiBadgeRenderer";
import { ICellRendererParams } from "ag-grid-enterprise";
import { TOOL_EDITOR_DEFAULT_COL_DEF } from "components/ToolEditor/constants";
import { CellEditorTags } from "./CellEditorTags";
import assert from "assert";
import { ToolEditorStoreState } from "components/ToolEditor/Provider/store/ToolEditorProvider.store";

const detailRendererStyle = css`
  width: 100%;
  .param-key {
    font-weight: 600;
  }
  min-height: 0px;
`;

export type ToolPathParamDetailRowDatum = {
  id: string;
  file_formats: string[] | undefined;
  tags: string[] | undefined;
};

type DetailColDef = ColDef<ToolPathParamDetailRowDatum> & {
  colId: "file_formats" | "tags";
};

export type DetailRendererToolPathParamProps = {
  data: NonNullable<IDetailCellRendererParams<ParamsEditorRowDatum>["data"]> & {
    type: Extract<
      NonNullable<
        IDetailCellRendererParams<ParamsEditorRowDatum>["data"]
      >["type"],
      { param_type: "ToolPathParam" }
    >;
  };
  updateSpecParam?: ToolEditorStoreState["updateSpecParam"];
  editMode?: boolean;
};

export const DetailRendererToolPathParam = ({
  data,
  editMode = true,
  updateSpecParam,
}: DetailRendererToolPathParamProps) => {
  const rowData = useMemo(() => {
    return [
      {
        id: data.key,
        file_formats: data.type.file_formats,
        tags: data.type.tags,
      },
    ];
  }, [data.key, data.type.file_formats, data.type.tags]);

  const columnDefs: DetailColDef[] = useMemo(() => {
    return [
      {
        colId: "file_formats" as const,
        headerName: "File Formats",
        cellEditor: (
          params: CustomCellEditorProps<ToolPathParamDetailRowDatum>,
        ) => (
          <CellEditorFileFormats
            onValueChange={params.onValueChange}
            stopEditing={params.stopEditing}
            data={params.data}
          />
        ),
        editable: editMode,
        suppressKeyboardEvent: (params) => {
          if (params.event.key === "Enter" && params.editing) {
            // suppress enter to close cell editor since combo box handles enter
            return true;
          }
          return false;
        },
        valueGetter: ({ data }) => data?.file_formats,
        cellRenderer: (
          params: ICellRendererParams<ToolPathParamDetailRowDatum>,
        ) => <MultiBadgeRenderer items={params.data?.file_formats ?? []} />,
      },
      {
        colId: "tags" as const,
        headerName: "Tags",
        cellEditor: (
          params: CustomCellEditorProps<ToolPathParamDetailRowDatum>,
        ) => (
          <CellEditorTags
            onValueChange={params.onValueChange}
            stopEditing={params.stopEditing}
            data={params.data}
          />
        ),
        editable: editMode,
        suppressKeyboardEvent: (params) => {
          if (params.event.key === "Enter" && params.editing) {
            // suppress enter to close cell editor since combo box handles enter
            return true;
          }
          return false;
        },
        valueGetter: ({ data }) => data?.tags,
        cellRenderer: (
          params: ICellRendererParams<ToolPathParamDetailRowDatum>,
        ) => (
          <MultiBadgeRenderer
            items={params.data?.tags ?? []}
            color={"hollow"}
          />
        ),
      },
    ];
  }, [editMode]);

  const getRowId = useCallback(
    ({ data }: { data: (typeof rowData)[number] }) => data.id,
    [],
  );

  const handleCellEditRequest = useCallback(
    (e: CellEditRequestEvent<(typeof rowData)[number]>) => {
      assert(updateSpecParam, "updateSpecParam is not defined");
      const newValue = e.newValue as CellEditRequestValue;

      const colId = e.column.getId() as DetailColDef["colId"];

      /**
       * Valid file formats
       */

      switch (colId) {
        case "file_formats":
          if (!isFileFormatsV3(newValue) && newValue !== undefined) {
            return;
          }
          break;
        case "tags":
          if (!isFileTagsV3(newValue) && newValue !== undefined) {
            return;
          }
          break;
      }

      updateSpecParam(
        data.key,
        "ToolPathParam",
        {
          [colId]: newValue,
        },
        true,
      );
    },
    [data.key, updateSpecParam],
  );

  return (
    <AgGridReact<(typeof rowData)[number]>
      className="ag-theme-balham-cell-borders"
      css={detailRendererStyle}
      getRowId={getRowId}
      domLayout="autoHeight"
      columnDefs={columnDefs}
      rowData={rowData}
      readOnlyEdit
      reactiveCustomComponents
      onCellEditRequest={editMode ? handleCellEditRequest : undefined}
      defaultColDef={TOOL_EDITOR_DEFAULT_COL_DEF}
    />
  );
};
